import React, { useContext, useEffect, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { RemindersContext } from "../contexts/RemindersContext";
import { format, differenceInDays, parseISO, startOfDay } from "date-fns";
import { FaCalendarAlt, FaCheck, FaTrashAlt, FaUndo } from "react-icons/fa";
import Navbar from "../components/Navbar";
import { useTranslation } from "react-i18next";

// --- ANIMATIONS ---
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// --- GLOBAL STYLES ---
const GlobalStyle = createGlobalStyle`
    body {
        margin: 0;
        padding: 0;
        scroll-behavior: smooth;
    }
`;

// --- MAIN CONTAINERS ---
const PageContainer = styled.div`
  padding: 24px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 3%;
  min-height: 100vh;
  color: var(--fontPrimaryColor);
  @media (max-width: 650px) {
    margin-top: 120px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 8px;
`;

// --- FORM STYLES ---
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 32px;
  background-color: var(--secondaryBackgroundColor);

  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  }

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  border: none;
  background: var(--thirdBackgroundColor);
  outline: none;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  outline: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  color: var(--fontPrimaryColor);
`;

const DateInput = styled(Input)`
  flex-basis: auto;
  color: var(--fontSecondaryColor);
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background-color: var(--primaryColor);
  color: white;
  font-weight: 500;
  font-family: "Montserrat", sans-serif;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  transition: background-color 0.5s ease, transform 0.1s ease,
    box-shadow 0.5s ease;

  &:hover {
    background-color: var(--darkPrimaryColor);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

// --- LIST STYLES ---
const ReminderList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 16px;

  transition: opacity 0.3s ease;
  ${(props) =>
    props.isHistory &&
    `
        opacity: 0.7;
    `}
`;

const Separator = styled.hr`
  margin: 32px 0;
  border-color: var(--fontSecondaryColor);
  transition: border-color 0.3s ease;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  background-color: var(--secondaryBackgroundColor);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  color: var(--fontSecondaryColor);
  font-size: 1.125rem;

  animation: ${slideIn} 0.5s ease-out;
`;

// --- REMINDER CARD STYLES ---
const ReminderCardContainer = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 12px;

  animation: ${slideIn} 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

  transition: background-color 0.4s ease, border-color 0.4s ease,
    color 0.4s ease, opacity 0.4s ease, transform 0.2s ease,
    box-shadow 0.2s ease;

  ${(props) =>
    props.isHistory
      ? `
        background-color: var(--secondaryBackgroundColor);
        border-left: 4px solid #9ca3af;
        color: #6b7280;
        opacity: 0.8;
    `
      : `
        background-color: var(--thirdBackgroundColor);
        border-left: 4px solid var(--primaryColor);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        
        &:hover {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
            transform: translateY(-2px);
        }
    `}
`;

const ReminderDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
`;

const ReminderTitle = styled.p`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: color 0.3s ease, text-decoration 0.3s ease;

  color: ${(props) =>
    props.isHistory ? "inherit" : "var(--fontPrimaryColor)"};
  text-decoration: ${(props) => (props.isCompleted ? "line-through" : "none")};
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
`;

const CalendarIcon = styled(FaCalendarAlt)`
  color: var(--primaryColor);
  transition: color 0.3s ease;
`;

const ReminderDateText = styled.p`
  transition: color 0.3s ease;
  color: ${(props) =>
    props.isHistory ? "var(--fontSecondaryColor)" : "var(--primaryColor)"};
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 16px;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const TimeDifferenceTag = styled.span`
  padding: 4px 8px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;

  transition: background-color 0.3s ease, color 0.3s ease;

  ${(props) =>
    props.isHistory &&
    `
        background-color: #e5e7eb;
        color: #4b5563;
    `}
  ${(props) =>
    !props.isHistory &&
    props.isToday &&
    `
        background-color: #d1fae5;
        color: #047857;
    `}
    ${(props) =>
    !props.isHistory &&
    !props.isToday &&
    `
        background-color: #c0d5e2;
        color: #555555;
        font-weight: 500;
    `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px;
  border-radius: 50%;
  background: none;
  border: 1px solid transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:active {
    transform: scale(0.9);
  }

  ${(props) =>
    props.variant === "complete" &&
    `
        color: #10b981; 
        background-color: #ecfdf5;
        &:hover {
            background-color: #10b981;
            color: white;
            transform: scale(1.1);
        }
    `}

  ${(props) =>
    props.variant === "undo" &&
    `
        color: #f59e0b; 
        &:hover {
            background-color: #fef3c7;
            transform: rotate(-45deg);
        }
    `}

    ${(props) =>
    props.variant === "delete" &&
    `
        color: #ef4444; 
        &:hover {
            background-color: #fee2e2;
            transform: scale(1.1);
        }
    `}
`;

// --- LOGIC HELPER ---
const getTimeDifference = (dateString, t) => {
  const date =
    typeof dateString === "string" ? parseISO(dateString) : dateString;
  const targetDate = startOfDay(date);
  const today = startOfDay(new Date());
  const diff = differenceInDays(targetDate, today);

  if (diff === 0) {
    return {
      text: t("reminders.today"),
      isPast: false,
      isToday: true,
      sortKey: 0,
    };
  } else if (diff === 1) {
    return {
      text: t("reminders.tomorrow"),
      isPast: false,
      isToday: false,
      sortKey: 1,
    };
  } else if (diff > 1) {
    return {
      text: t("reminders.inDays", { days: diff }),
      isPast: false,
      isToday: false,
      sortKey: diff,
    };
  } else if (diff === -1) {
    return {
      text: t("reminders.yesterday"),
      isPast: true,
      isToday: false,
      sortKey: 1,
    };
  } else {
    const daysAgo = Math.abs(diff);
    return {
      text: t("reminders.daysAgo", { days: daysAgo }),
      isPast: true,
      isToday: false,
      sortKey: daysAgo,
    };
  }
};

// --- REMINDER CARD COMPONENT ---
const ReminderCard = ({ reminder, deleteReminder, toggleReminder, t }) => {
  const dateString =
    reminder.date instanceof Date
      ? format(reminder.date, "yyyy-MM-dd")
      : reminder.date;

  const { text, isPast, isToday } = getTimeDifference(dateString, t);

  const isHistory = isPast || reminder.isCompleted;

  return (
    <ReminderCardContainer isHistory={isHistory}>
      <ReminderDetails>
        <TitleRow>
          <ReminderTitle
            isHistory={isHistory}
            isCompleted={reminder.isCompleted}
          >
            {reminder.title}
          </ReminderTitle>
        </TitleRow>
        <DateRow>
          <CalendarIcon />
          <ReminderDateText isHistory={isHistory}>
            {format(parseISO(dateString), "MMM d, yyyy")}
          </ReminderDateText>
        </DateRow>
      </ReminderDetails>

      <ActionsContainer>
        <InfoColumn>
          <TimeDifferenceTag isHistory={isHistory} isToday={isToday}>
            {reminder.isCompleted ? t("reminders.done") : text}
          </TimeDifferenceTag>
        </InfoColumn>

        <ButtonGroup>
          {!reminder.isCompleted && !isPast && (
            <ActionButton
              variant="complete"
              onClick={() => toggleReminder(reminder.id)}
              title={t("reminders.markDone")}
            >
              <FaCheck size={14} />
            </ActionButton>
          )}

          {reminder.isCompleted && (
            <ActionButton
              variant="undo"
              onClick={() => toggleReminder(reminder.id)}
              title={t("reminders.undo")}
            >
              <FaUndo size={14} />
            </ActionButton>
          )}

          <ActionButton
            variant="delete"
            onClick={() => deleteReminder(reminder.id)}
            title={t("reminders.delete")}
          >
            <FaTrashAlt size={14} />
          </ActionButton>
        </ButtonGroup>
      </ActionsContainer>
    </ReminderCardContainer>
  );
};

// --- MAIN PAGE ---
export default function RemindersPage({ theme, switchTheme }) {
  const { t } = useTranslation();
  const { reminders, addReminder, deleteReminder, toggleReminder } =
    useContext(RemindersContext);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!title || !date) return;
    await addReminder(title, date);
    setTitle("");
    setDate("");
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Reminders - ASites Planner";
  }, []);

  // --- SORTING & FILTERING ---
  const remindersWithTime = reminders.map((r) => ({
    ...r,
    timeData: getTimeDifference(r.date, t),
  }));

  const upcoming = remindersWithTime
    .filter((r) => !r.timeData.isPast && !r.isCompleted)
    .sort((a, b) => a.timeData.sortKey - b.timeData.sortKey);

  const history = remindersWithTime
    .filter((r) => r.timeData.isPast || r.isCompleted)
    .sort((a, b) => a.timeData.sortKey - b.timeData.sortKey);

  return (
    <>
      <Navbar theme={theme} switchTheme={switchTheme} />
      <GlobalStyle />
      <PageContainer>
        <Title>{t("reminders.title")}</Title>
        <Subtitle>{t("reminders.subtitle")}</Subtitle>

        <Form onSubmit={handleAdd}>
          <Input
            type="text"
            placeholder={t("reminders.placeholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <DateInput
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <SubmitButton type="submit">
            {t("reminders.addReminder")}
          </SubmitButton>
        </Form>

        {/* UPCOMING */}
        {upcoming.length > 0 && (
          <div className="mb-8">
            <SectionTitle>
              {t("reminders.upcoming")} ({upcoming.length})
            </SectionTitle>
            <ReminderList>
              {upcoming.map((r) => (
                <ReminderCard
                  key={r.id}
                  reminder={r}
                  deleteReminder={deleteReminder}
                  toggleReminder={toggleReminder}
                  t={t}
                />
              ))}
            </ReminderList>
          </div>
        )}

        {/* SEPARATOR */}
        {upcoming.length > 0 && history.length > 0 && <Separator />}

        {/* PAST / HISTORY */}
        {history.length > 0 && (
          <div className="mb-8">
            <SectionTitle isHistory>
              {t("reminders.history")} ({history.length})
            </SectionTitle>
            <ReminderList>
              {history.map((r) => (
                <ReminderCard
                  key={r.id}
                  reminder={r}
                  deleteReminder={deleteReminder}
                  toggleReminder={toggleReminder}
                  t={t}
                />
              ))}
            </ReminderList>
          </div>
        )}

        {/* EMPTY */}
        {reminders.length === 0 && (
          <EmptyState>{t("reminders.emptyState")}</EmptyState>
        )}
      </PageContainer>
    </>
  );
}
